import styles from './ProductsSoldTable.module.css';
import React, { useEffect, useRef,  } from 'react';
import { ProductsSold } from '@boxem/core-api-schema';
import Skeleton from '@/components/Skeleton/Skeleton';
import TextInput from '@/components/forms/TextInput/TextInput';
import { Table, TableColumn } from '@/components/Table/Table';
import { useCategoryAnalytics } from '@/contexts/CategoryAnalyticsContext';
import { useParams } from 'wouter';
import { formatValue } from '@/utils/formatUtils';
import Pagination from '@/components/Pagination/Pagination';
import IconSearch from '@/components/icons/IconSearch';

export default function ProductsSoldTable() {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableName = "categoryProductsSoldTable";
  const params = useParams<{ category: string}>();
  
  const {
    isProductsSoldLoading,
    filteredAndPaginatedProductsSold,
    productsSoldLimit,
    productsSoldSortDirection,
    setProductsSoldSort,
    setProductsSoldLimit,
    setProductsSoldPage,
    timeFrame,
    fetchProductsSold,
    setProductsSoldSearch,
    productsSoldSearch
} = useCategoryAnalytics();

  useEffect(() => {
    fetchProductsSold(decodeURIComponent(params.category));
  }, [params.category, timeFrame])

  const columns: TableColumn<ProductsSold>[] = [
    {
      title: 'Products',
      dataKey: 'name',
      id: 'products',
      sortable: true,
      width: '19rem',
      pinned: 'left',
      renderRowCell: (row) => (
        <div className={styles.listingItem}>
          <img src={row.getCellData('image')} alt={row.getCellData('name')} className={styles.listingImage} />
          <div className={styles.listingItemDetail}>
            <div className={styles.listingName}>{row.getCellData('name')}</div>
            <span className={styles.listingSku}>{row.getCellData('sku')}</span>
          </div>
        </div>
      ),
      renderSkeleton: () => <div className={styles.listingItem}>
        <Skeleton type='image' height={32} width={32} />
        <div className={styles.listingItemDetail}>
          <Skeleton type='line-medium' />
          <Skeleton type='line-short' />
        </div>
      </div>
    },
    {
      title: 'List price',
      dataKey: 'listPrice',
      id: 'listPrice',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => {
        return (
            <div className={styles.listingItem}>
              {formatValue(row.getCellData('listPrice'), 'currency')}
            </div>
        )
      }
    },
    {
      title: 'Revenue',
      dataKey: 'revenue',
      id: 'revenue',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('revenue'), 'currency'),
    },
    {
      title: 'Profit',
      dataKey: 'profit',
      id: 'profit',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('profit'), 'currency'),
    },
    {
        title: 'Units sold',
        dataKey: 'unitsSold',
        id: 'unitsSold',
        sortable: true,
        width: '7.5rem',
        visible: true,
        renderRowCell: (row) => formatValue(row.getCellData('UnitsSold'), 'number'),
      },
    {
      title: 'Refunds',
      dataKey: 'refunds',
      id: 'refunds',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('refunds'), 'number'),
    },

    {
      title: 'Buy cost',
      dataKey: 'buyCosts',
      id: 'buyCosts',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('buyCosts'), 'currency'),
    },
  ];

  return (
    <div className={styles.tableContainer}>
      <Table
        data={filteredAndPaginatedProductsSold?.items || []}
        tableContainerRef={tableContainerRef}
        columns={columns}
        tableName={tableName}
        isLoading={isProductsSoldLoading}
        skeletonRows={productsSoldLimit}
        sortDirection={productsSoldSortDirection}
        sortedColumnId={productsSoldSortDirection}
        onSort={setProductsSoldSort}
        showSort
        header={<TextInput
            className={styles.searchInput}
            placeholder={"Search By Asin, Sku"}
            prefix={<IconSearch/>}
            value={productsSoldSearch}
            inputAutofocus
            onChange={(value) => setProductsSoldSearch(value)}
        />}
        footer={<Pagination
          total={filteredAndPaginatedProductsSold?.total || 0}
          pageSize={filteredAndPaginatedProductsSold?.limit || 5}
          onChangePageSize={(value) => setProductsSoldLimit(value)}
          page={filteredAndPaginatedProductsSold?.page || 1}
          onChangePage={(newPage) => setProductsSoldPage(newPage)}
          showPageSelector={false}
          pageSizeOptions={[5, 10, 20]}
        />} />
    </div>
  );
}
